import { agentControllerRefSchema } from "../daemon/agent-controller-ref.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { runtimeTransportModeSchema } from "../daemon/controller-contract.js";
import { agentControllerInterfaceSnapshotSchema } from "../runtime/agent-controller-interface.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/runtime-reporting.ts
const boundedIdentifierSchema = z$1.string().trim().min(1).max(256);
const reasonCodeSchema = z$1.string().trim().min(1).max(256);
const AGENT_CONTROLLER_TASK_LIFECYCLE_EVENT_BATCH_MAX_BYTES = 128 * 1024;
const runtimeReportingRouteParamsSchema = z$1.strictObject({ deviceId: boundedIdentifierSchema });
const runtimeReportingRuntimeIdSchema = z$1.strictObject({
	source: z$1.literal("runtime_registry_authority"),
	value: z$1.string().trim().min(1).max(512)
});
const submitRuntimeRegistrationBodySchema = z$1.strictObject({ lane: boundedIdentifierSchema });
const agentControllerTaskLifecycleEventTypeSchema = z$1.enum([
	"registered",
	"completed",
	"needs_attention"
]);
const agentControllerTaskLifecycleEventSourceSchema = z$1.enum([
	"claude_notification",
	"claude_session_start",
	"claude_subagent_start",
	"claude_subagent_stop",
	"claude_task_completed",
	"claude_task_created",
	"codex_permission_request",
	"codex_stop",
	"codex_subagent_start",
	"codex_subagent_stop",
	"codex_user_prompt_submit"
]);
const submitAgentControllerTaskLifecycleEventItemSchema = z$1.strictObject({
	agentControllerRef: agentControllerRefSchema,
	attentionCode: reasonCodeSchema.nullable(),
	eventId: boundedIdentifierSchema,
	eventType: agentControllerTaskLifecycleEventTypeSchema,
	occurredAtMs: z$1.int().nonnegative(),
	organizationId: boundedIdentifierSchema,
	source: agentControllerTaskLifecycleEventSourceSchema,
	taskId: boundedIdentifierSchema
}).superRefine((value, context) => {
	if (value.eventType === "needs_attention" && value.attentionCode === null) context.addIssue({
		code: "custom",
		message: "needs_attention lifecycle events require an attentionCode.",
		path: ["attentionCode"]
	});
	if (value.eventType !== "needs_attention" && value.attentionCode !== null) context.addIssue({
		code: "custom",
		message: "Only needs_attention lifecycle events may carry attentionCode.",
		path: ["attentionCode"]
	});
});
const submitAgentControllerTaskLifecycleEventsBodySchema = z$1.strictObject({
	events: z$1.array(submitAgentControllerTaskLifecycleEventItemSchema).min(1).max(64),
	lane: boundedIdentifierSchema,
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema
});
const submitAgentControllerTaskLifecycleEventsRpcInputSchema = z$1.strictObject({
	...submitAgentControllerTaskLifecycleEventsBodySchema.shape,
	context: openMeldRequestContextSchema.optional(),
	deviceId: boundedIdentifierSchema
});
const runtimeRegistrationRejectedReasonSchema = z$1.enum([
	"device_forgotten",
	"owner_authorization_denied",
	"owner_authorization_unknown",
	"owner_authorization_unavailable",
	"owner_kind_unsupported",
	"openmeld_service_runtime_id_collision",
	"openmeld_service_runtime_id_invalid",
	"openmeld_service_runtime_id_not_opaque",
	"runtime_record_missing_after_conflict",
	"unknown"
]);
const submitRuntimeRegistrationResponseSchema = z$1.strictObject({
	reason: runtimeRegistrationRejectedReasonSchema.optional(),
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema.optional(),
	status: z$1.enum([
		"created",
		"reused",
		"rejected"
	])
}).superRefine((value, context) => {
	if (value.status !== "rejected" && !value.openMeldServiceRuntimeId) context.addIssue({
		code: "custom",
		message: "registered Runtime response requires authority openMeldServiceRuntimeId",
		path: ["openMeldServiceRuntimeId"]
	});
	if (value.status === "rejected" && !value.reason) context.addIssue({
		code: "custom",
		message: "rejected Runtime registration requires a reason",
		path: ["reason"]
	});
});
const runtimeAgentControllerReportStatusSchema = z$1.enum([
	"ready",
	"disabled",
	"unavailable",
	"unknown"
]);
const submitRuntimeAgentControllerReportItemSchema = z$1.strictObject({
	agentControllerRef: boundedIdentifierSchema,
	agentControllerInterface: agentControllerInterfaceSnapshotSchema.optional(),
	expiresAtMs: z$1.int().positive(),
	observedAtMs: z$1.int().positive(),
	reasonCodes: z$1.array(reasonCodeSchema).optional(),
	status: runtimeAgentControllerReportStatusSchema
}).superRefine((value, context) => {
	if (value.agentControllerInterface && value.agentControllerInterface.agentControllerRef !== value.agentControllerRef) context.addIssue({
		code: "custom",
		message: "agentControllerInterface.agentControllerRef must match report agentControllerRef",
		path: ["agentControllerInterface", "agentControllerRef"]
	});
});
const submitRuntimeAgentControllerReportsBodySchema = z$1.strictObject({
	lane: boundedIdentifierSchema,
	reports: z$1.array(submitRuntimeAgentControllerReportItemSchema).min(1).max(64),
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema
});
const runtimeProviderConversationProofProviderSchema = z$1.literal("codex");
const runtimeProviderConversationProofMethodSchema = z$1.literal("codex_app_server_thread_resume");
const runtimeProviderConversationProofStatusSchema = z$1.enum(["failed", "passed"]);
const submitRuntimeProviderConversationProofItemSchema = z$1.strictObject({
	agentProfileId: boundedIdentifierSchema,
	bindingId: boundedIdentifierSchema,
	agentControllerRef: boundedIdentifierSchema,
	agentControllerConversationId: boundedIdentifierSchema,
	transportMode: runtimeTransportModeSchema,
	provider: runtimeProviderConversationProofProviderSchema,
	method: runtimeProviderConversationProofMethodSchema,
	intrusive: z$1.literal(false),
	observedAtMs: z$1.int().positive(),
	expiresAtMs: z$1.int().positive(),
	status: runtimeProviderConversationProofStatusSchema,
	errorCode: boundedIdentifierSchema.optional(),
	errorMessage: z$1.string().trim().min(1).max(2e3).optional(),
	errorType: boundedIdentifierSchema.optional()
});
const submitRuntimeProviderConversationProofsBodySchema = z$1.strictObject({
	lane: boundedIdentifierSchema,
	proofs: z$1.array(submitRuntimeProviderConversationProofItemSchema).min(1).max(64),
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema
});
const runtimeAgentControllerReportRejectedReasonSchema = z$1.enum([
	"agent_controller_ref_invalid",
	"conflict",
	"out_of_order",
	"report_status_invalid",
	"report_time_invalid",
	"openmeld_service_runtime_id_invalid",
	"openmeld_service_runtime_id_mismatch",
	"openmeld_service_runtime_identity_invalid",
	"runtime_lookup_conflict",
	"runtime_missing",
	"stale"
]);
const agentControllerTaskLifecycleEventRejectedReasonSchema = z$1.enum([
	"agent_controller_ref_unsupported",
	"event_id_conflict",
	"event_source_invalid",
	"event_time_invalid",
	"organization_membership_denied",
	"organization_membership_unavailable",
	"registered_task_missing",
	"runtime_lookup_conflict",
	"runtime_missing",
	"openmeld_service_runtime_id_invalid",
	"openmeld_service_runtime_id_mismatch",
	"openmeld_service_runtime_identity_invalid"
]);
const runtimeProviderConversationProofRejectedReasonSchema = z$1.enum([
	"agent_controller_ref_invalid",
	"agent_profile_binding_mismatch",
	"agent_profile_binding_missing",
	"openmeld_service_runtime_id_invalid",
	"openmeld_service_runtime_id_mismatch",
	"openmeld_service_runtime_identity_invalid",
	"binding_id_invalid",
	"conflict",
	"out_of_order",
	"proof_status_invalid",
	"proof_time_invalid",
	"provider_method_invalid",
	"provider_unsupported",
	"runtime_lookup_conflict",
	"runtime_missing",
	"stale"
]);
const submitRuntimeAgentControllerReportResultSchema = z$1.strictObject({
	agentControllerRef: boundedIdentifierSchema,
	outcome: z$1.enum([
		"created",
		"updated",
		"reused"
	]).optional(),
	reason: runtimeAgentControllerReportRejectedReasonSchema.optional(),
	status: z$1.enum(["accepted", "rejected"])
}).superRefine((value, context) => {
	if (value.status === "accepted" && !value.outcome) context.addIssue({
		code: "custom",
		message: "accepted Runtime Agent Controller Report requires an outcome",
		path: ["outcome"]
	});
	if (value.status === "rejected" && !value.reason) context.addIssue({
		code: "custom",
		message: "rejected Runtime Agent Controller Report requires a reason",
		path: ["reason"]
	});
});
const submitRuntimeAgentControllerReportsResponseSchema = z$1.strictObject({
	reports: z$1.array(submitRuntimeAgentControllerReportResultSchema),
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema.optional(),
	status: z$1.enum([
		"accepted",
		"partial",
		"rejected"
	])
}).superRefine((value, context) => {
	if (value.status !== "rejected" && !value.openMeldServiceRuntimeId) context.addIssue({
		code: "custom",
		message: "accepted Runtime Agent Controller Report response requires authority openMeldServiceRuntimeId",
		path: ["openMeldServiceRuntimeId"]
	});
});
const submitAgentControllerTaskLifecycleEventResultSchema = z$1.strictObject({
	eventId: boundedIdentifierSchema,
	outcome: z$1.enum(["accepted", "reused"]).optional(),
	reason: agentControllerTaskLifecycleEventRejectedReasonSchema.optional(),
	status: z$1.enum([
		"accepted",
		"permanently_rejected",
		"retryable_rejected"
	])
}).superRefine((value, context) => {
	if (value.status === "accepted" && !value.outcome) context.addIssue({
		code: "custom",
		message: "Accepted Agent Controller task lifecycle events require an outcome.",
		path: ["outcome"]
	});
	if (value.status === "accepted" && value.reason) context.addIssue({
		code: "custom",
		message: "Accepted Agent Controller task lifecycle events cannot carry a rejection reason.",
		path: ["reason"]
	});
	if (value.status !== "accepted" && !value.reason) context.addIssue({
		code: "custom",
		message: "Rejected Agent Controller task lifecycle events require a reason.",
		path: ["reason"]
	});
	if (value.status !== "accepted" && value.outcome) context.addIssue({
		code: "custom",
		message: "Rejected Agent Controller task lifecycle events cannot carry an accepted outcome.",
		path: ["outcome"]
	});
});
const submitAgentControllerTaskLifecycleEventsResponseSchema = z$1.strictObject({
	events: z$1.array(submitAgentControllerTaskLifecycleEventResultSchema),
	status: z$1.enum([
		"accepted",
		"partial",
		"rejected"
	])
});
const submitRuntimeProviderConversationProofResultSchema = z$1.strictObject({
	agentProfileId: boundedIdentifierSchema,
	bindingId: boundedIdentifierSchema,
	outcome: z$1.enum([
		"created",
		"updated",
		"reused"
	]).optional(),
	reason: runtimeProviderConversationProofRejectedReasonSchema.optional(),
	status: z$1.enum(["accepted", "rejected"])
}).superRefine((value, context) => {
	if (value.status === "accepted" && !value.outcome) context.addIssue({
		code: "custom",
		message: "accepted Provider Conversation Proof requires an outcome",
		path: ["outcome"]
	});
	if (value.status === "rejected" && !value.reason) context.addIssue({
		code: "custom",
		message: "rejected Provider Conversation Proof requires a reason",
		path: ["reason"]
	});
});
const submitRuntimeProviderConversationProofsResponseSchema = z$1.strictObject({
	proofs: z$1.array(submitRuntimeProviderConversationProofResultSchema),
	openMeldServiceRuntimeId: runtimeReportingRuntimeIdSchema.optional(),
	status: z$1.enum([
		"accepted",
		"partial",
		"rejected"
	])
}).superRefine((value, context) => {
	if (value.status !== "rejected" && !value.openMeldServiceRuntimeId) context.addIssue({
		code: "custom",
		message: "accepted Provider Conversation Proof response requires authority openMeldServiceRuntimeId",
		path: ["openMeldServiceRuntimeId"]
	});
});
//#endregion
export { AGENT_CONTROLLER_TASK_LIFECYCLE_EVENT_BATCH_MAX_BYTES, agentControllerTaskLifecycleEventRejectedReasonSchema, agentControllerTaskLifecycleEventSourceSchema, agentControllerTaskLifecycleEventTypeSchema, runtimeAgentControllerReportRejectedReasonSchema, runtimeAgentControllerReportStatusSchema, runtimeProviderConversationProofMethodSchema, runtimeProviderConversationProofProviderSchema, runtimeProviderConversationProofRejectedReasonSchema, runtimeProviderConversationProofStatusSchema, runtimeRegistrationRejectedReasonSchema, runtimeReportingRouteParamsSchema, runtimeReportingRuntimeIdSchema, submitAgentControllerTaskLifecycleEventItemSchema, submitAgentControllerTaskLifecycleEventResultSchema, submitAgentControllerTaskLifecycleEventsBodySchema, submitAgentControllerTaskLifecycleEventsResponseSchema, submitAgentControllerTaskLifecycleEventsRpcInputSchema, submitRuntimeAgentControllerReportItemSchema, submitRuntimeAgentControllerReportResultSchema, submitRuntimeAgentControllerReportsBodySchema, submitRuntimeAgentControllerReportsResponseSchema, submitRuntimeProviderConversationProofItemSchema, submitRuntimeProviderConversationProofResultSchema, submitRuntimeProviderConversationProofsBodySchema, submitRuntimeProviderConversationProofsResponseSchema, submitRuntimeRegistrationBodySchema, submitRuntimeRegistrationResponseSchema };

//# sourceMappingURL=runtime-reporting.js.map